/**
 * The roster. Server-owned, shared with the client for VFX and telegraphs.
 *
 * THE CLIENT NEVER SUPPLIES A NUMBER FROM THIS FILE. It reads these values to
 * draw a cooldown ring, size a telegraph decal, and pick a color. Every value
 * that decides an outcome is read by the server from this same table, and the
 * server does not trust a client's copy of it.
 *
 * Tuning lives here and only here, so a balance pass is one file and one
 * deploy.
 *
 * ── TICKERS ARE DISPLAY NAMES. IDS ARE NOT. ───────────────────────────────
 *
 * The roster reads NVDA / AAPL / GOOGL / TSLA / SPCX on screen, and `volt` /
 * `monolith` / `hex` / `mirage` / `talon` in every identifier below. That is
 * deliberate and it is not laziness:
 *
 *   - `characterId` is a String column on the match table and a field in every
 *     replay header. Renaming the VALUES would orphan every stored row and
 *     every archived replay — evidence a dispute is settled from. A theme is
 *     not worth invalidating the audit trail.
 *   - The archetypes outlive the theme. `volt` is the assassin whatever ticker
 *     is painted on it, and STOCK-THEME §2 says exactly that: the ticker
 *     inherits the archetype, once, by hand.
 *
 * The same split runs one level down: ability IDS keep their archetype names,
 * so the code and its comments still say `fault_line` where the UI says
 * "Supercycle". The bridge, once, here — grep either column and land in the
 * right place:
 *
 *   NVDA   blink_strike  Parallel Path  | overload      Overclock      | chain_lightning CUDA Cascade
 *   AAPL   seismic_slam  One More Thing | bulwark       Walled Garden  | fault_line      Supercycle
 *   GOOGL  glyph_trap    Tracking Pixel | gravity_well  PageRank       | null_zone       Deindexed
 *   TSLA   phantom       Autopilot      | fade          Ludicrous Mode | displace        Robotaxi
 *   SPCX   arc_shot      Falcon         | perch         Hover Slam     | barrage         Starlink
 *
 * So this pass touches `name`, `fantasy` and `description` and NOTHING ELSE.
 * Every health, speed, damage, cooldown, windup, recovery and radius below is
 * byte-for-byte what it was — STOCK-THEME §2 calls this out as the property
 * that makes the re-skin free, and it is only free if it stays true.
 *
 * `colorHex` IS THE CHARACTER'S IDENTITY, and there is exactly one of it.
 *
 * It was a separate abstract palette — cyan / orange / violet / mint / amber —
 * chosen for maximum separability back when nothing else carried a character's
 * colour. Then the procedural bodies arrived with their own family palettes and
 * the game started disagreeing with itself: a player's body was deep green
 * while their nameplate, telegraph and lobby card were cyan. Two sources of
 * truth for "what colour is this character", which is one too many.
 *
 * These are now the family colours from the bodies (see `blueprint.js`): bull
 * green, vault gold, bear maroon, android chrome, satbot blue. The lobby, the
 * cards, the nameplates and the models all derive from this one value.
 *
 * Still deliberately NOT the corporate palettes — taking a company's exact
 * brand colour along with its name reads a good deal more like a claim of
 * affiliation than a ticker does on its own.
 *
 * WHAT THIS VALUE IS NOT: a colour it is safe to paint on the arena floor. See
 * `telegraphHex` below — that is a different question with a different answer,
 * and collapsing the two is how you get a maroon danger zone drawn next to a
 * red hazard pool.
 */
/**
 * The one place the body/world split is decided. The sim's hit resolution and
 * the client's telegraph/VFX both consult this — two independent copies of
 * this rule is exactly the drift it exists to prevent.
 *
 * Derived from the hit shape rather than authored per-ability, so a new
 * ability cannot forget to declare it: ground zones and projectiles are places
 * by construction, and a dash_line that does not move its caster (Fault Line)
 * is a crack drawn on the floor. Everything else — melee cones, spheres,
 * self-casts, and dashes the caster rides (Blink Strike) — is the body.
 */
export function abilityAnchor(ability) {
    const kind = ability.hit.kind;
    if (kind === "ground_zone" || kind === "projectile")
        return "world";
    if (kind === "dash_line" && !(ability.selfMove && ability.selfMove > 0))
        return "world";
    return "body";
}
const BASIC = (damage, range = 2.6) => ({
    slot: "basic",
    id: "basic",
    name: "Strike",
    damage,
    cooldownMs: 550,
    windupMs: 90,
    recoveryMs: 140,
    hit: { kind: "melee_cone", range, halfAngleDeg: 55 },
    description: "Quick attack. No cooldown pressure — closes the gap between ability windows.",
});
/**
 * The colour a character's ability may be drawn on the ground in.
 *
 * Derived from `colorHex`, and deliberately not equal to it. The arena deck
 * already spends two hues on rules every player must read instantly: red
 * (#ff3d3d) is a hazard pool that burns you and green (#4ade80) is a mend pad
 * that heals you. Those are fixed across every arena precisely so they never
 * have to be relearned.
 *
 * A telegraph tinted bear-maroon lands next to a hazard lane; one tinted
 * bull-green lands next to a mend pad. Under pressure, at phone size, on a
 * floor you are already reading for threat, that is a misread with damage
 * attached.
 *
 * So identity and floor-safety are separated. Identity wants the body colour;
 * the floor wants distance from the signal hues. This lifts everything toward
 * bright and cyan-to-violet, which is far from both signals, while keeping
 * enough of the source hue that a player still recognises whose ability it is.
 *
 * Kept beside the roster rather than in the renderer because it is a fact
 * about the CHARACTER, and the client is not the only thing that may want it.
 */
const TELEGRAPH_HEX = {
    volt: 0x5ce1ff, // green identity, cyan on the floor
    monolith: 0xffb84d, // gold reads as amber, already clear of both signals
    hex: 0xc46cff, // maroon would sit on the hazard hue; violet does not
    mirage: 0xa8e6ff, // chrome has no hue of its own, so borrow a cold one
    talon: 0x4d9dff, // blue is already safe; brightened for the deck
};
export function telegraphHex(id) {
    return TELEGRAPH_HEX[id];
}
export const CHARACTERS = {
    volt: {
        id: "volt",
        name: "NVDA",
        archetype: "assassin",
        maxHealth: 80,
        speedMult: 1.25,
        damageMult: 1.15,
        colorHex: 0x3da94c,
        fantasy: "Highest beta on the board. Deletes the wounded, or gets deleted trying.",
        abilities: {
            basic: BASIC(8),
            skill1: {
                slot: "skill1",
                id: "blink_strike",
                name: "Parallel Path",
                damage: 28,
                cooldownMs: 8000,
                windupMs: 120,
                recoveryMs: 180,
                hit: { kind: "dash_line", length: 9, width: 1.6 },
                selfMove: 9,
                effects: [{ kind: "refund_cooldown_on_kill", pct: 50 }],
                description: "Dash through a target. Refunds half the cooldown on a kill.",
            },
            skill2: {
                slot: "skill2",
                id: "overload",
                name: "Overclock",
                damage: 0,
                cooldownMs: 12000,
                windupMs: 0,
                recoveryMs: 0,
                hit: { kind: "self" },
                effects: [{ kind: "speed", pct: 40, durationMs: 2000 }],
                description: "2s of +40% move speed and slow immunity.",
            },
            ultimate: {
                slot: "ultimate",
                id: "chain_lightning",
                name: "CUDA Cascade",
                damage: 45,
                cooldownMs: 0,
                windupMs: 350,
                recoveryMs: 300,
                hit: { kind: "sphere", radius: 11 },
                effects: [{ kind: "chain", maxTargets: 3, falloffPct: 60, radius: 8 }],
                description: "Strikes the nearest target, arcing to two more at 60% falloff.",
            },
        },
    },
    monolith: {
        id: "monolith",
        name: "AAPL",
        archetype: "juggernaut",
        maxHealth: 150,
        speedMult: 0.85,
        damageMult: 1.0,
        colorHex: 0xd9a62e,
        fantasy: "Slow, enormous, and extremely hard to remove. Walks in and stays.",
        abilities: {
            basic: BASIC(8, 3.0),
            skill1: {
                slot: "skill1",
                id: "seismic_slam",
                name: "One More Thing",
                damage: 30,
                cooldownMs: 9000,
                windupMs: 400,
                recoveryMs: 320,
                hit: { kind: "sphere", radius: 6 },
                knockback: 4,
                description: "Ground slam. Damages and knocks back everything within 6m.",
            },
            skill2: {
                slot: "skill2",
                id: "bulwark",
                name: "Walled Garden",
                damage: 0,
                cooldownMs: 14000,
                windupMs: 150,
                recoveryMs: 0,
                hit: { kind: "self" },
                effects: [
                    { kind: "damage_reduction", pct: 50, durationMs: 3000 },
                    { kind: "no_dash", durationMs: 3000 },
                ],
                description: "3s of 50% damage reduction. Cannot dash while active.",
            },
            ultimate: {
                slot: "ultimate",
                id: "fault_line",
                name: "Supercycle",
                damage: 55,
                cooldownMs: 0,
                windupMs: 600,
                recoveryMs: 400,
                hit: { kind: "dash_line", length: 14, width: 3 },
                effects: [{ kind: "stun", durationMs: 2000 }],
                description: "Cracks the ground in a line. Heavy damage and a 2s stun.",
            },
        },
    },
    hex: {
        id: "hex",
        name: "GOOGL",
        archetype: "controller",
        maxHealth: 95,
        speedMult: 1.0,
        damageMult: 0.9,
        colorHex: 0xd14a3e,
        fantasy: "Never chases you. Owns the ground you were going to stand on.",
        abilities: {
            basic: BASIC(8),
            skill1: {
                slot: "skill1",
                id: "glyph_trap",
                name: "Tracking Pixel",
                damage: 22,
                cooldownMs: 7000,
                windupMs: 250,
                recoveryMs: 180,
                hit: { kind: "ground_zone", radius: 3, durationMs: 12000 },
                effects: [{ kind: "slow", pct: 50, durationMs: 2000 }],
                description: "Arms a rune. Damages and slows whoever triggers it.",
            },
            skill2: {
                slot: "skill2",
                id: "gravity_well",
                name: "PageRank",
                damage: 0,
                cooldownMs: 13000,
                windupMs: 400,
                recoveryMs: 250,
                hit: { kind: "sphere", radius: 8 },
                effects: [{ kind: "pull", strength: 6 }],
                description: "Drags everyone within 8m toward a point over one second.",
            },
            ultimate: {
                slot: "ultimate",
                id: "null_zone",
                name: "Deindexed",
                damage: 0,
                cooldownMs: 0,
                windupMs: 500,
                recoveryMs: 300,
                hit: { kind: "ground_zone", radius: 10, durationMs: 5000 },
                effects: [
                    { kind: "suppress_abilities", durationMs: 5000 },
                    { kind: "no_dash", durationMs: 5000 },
                    { kind: "damage_over_time", dps: 8, durationMs: 5000 },
                ],
                description: "A dome where nobody may ult or dash, ticking damage.",
            },
        },
    },
    mirage: {
        id: "mirage",
        name: "TSLA",
        archetype: "trickster",
        maxHealth: 90,
        speedMult: 1.1,
        damageMult: 1.0,
        colorHex: 0xe6ebf2,
        fantasy: "You are never certain which one is real. The 1v3 you should have lost.",
        abilities: {
            basic: BASIC(8),
            skill1: {
                slot: "skill1",
                id: "phantom",
                name: "Autopilot",
                damage: 0,
                cooldownMs: 10000,
                windupMs: 120,
                recoveryMs: 100,
                hit: { kind: "self" },
                effects: [{ kind: "decoy", durationMs: 4000 }],
                description: "Spawns a decoy replaying your last 2s of movement. Deals no damage.",
            },
            skill2: {
                slot: "skill2",
                id: "fade",
                name: "Ludicrous Mode",
                damage: 0,
                cooldownMs: 11000,
                windupMs: 0,
                recoveryMs: 0,
                hit: { kind: "self" },
                effects: [
                    { kind: "invisible", durationMs: 1500 },
                    { kind: "speed", pct: 25, durationMs: 1500 },
                ],
                description: "1.5s of near-invisibility — a faint shimmer remains — plus speed.",
            },
            ultimate: {
                slot: "ultimate",
                id: "displace",
                name: "Robotaxi",
                damage: 0,
                cooldownMs: 0,
                windupMs: 300,
                recoveryMs: 200,
                hit: { kind: "sphere", radius: 20 },
                effects: [{ kind: "swap_position" }],
                description: "Swap positions with any target within 20m.",
            },
        },
    },
    talon: {
        id: "talon",
        name: "SPCX",
        archetype: "artillery",
        maxHealth: 85,
        speedMult: 1.05,
        damageMult: 1.1,
        colorHex: 0x2e7fd9,
        fantasy: "Pressure from somewhere you would rather it weren't. Nobody is near you.",
        abilities: {
            basic: BASIC(8),
            skill1: {
                slot: "skill1",
                id: "arc_shot",
                name: "Falcon",
                damage: 26,
                cooldownMs: 5000,
                windupMs: 200,
                recoveryMs: 180,
                // The sim launches at 15 degrees (PROJECTILE_LAUNCH_* in @chaos/sim):
                // flat enough that the shell threatens standing bodies along its whole
                // flight instead of only at a distant landing ring. Against g=19 that
                // carries ~19m on flat ground; height under the caster — a Perch —
                // stretches the arc toward `maxRange`.
                hit: { kind: "projectile", speed: 24, radius: 2.5, maxRange: 30, gravity: 19 },
                description: "Flat-arced shell with a 2.5m splash on impact.",
            },
            skill2: {
                slot: "skill2",
                id: "perch",
                name: "Hover Slam",
                damage: 0,
                cooldownMs: 12000,
                windupMs: 100,
                recoveryMs: 0,
                hit: { kind: "self" },
                selfMove: 8,
                effects: [{ kind: "hover", durationMs: 2000 }],
                // No hidden range stat: the altitude itself is what stretches Arc
                // Shot's ballistic carry. The tooltip promises only what the sim does.
                description: "Leap 8m up and hover — high ground carries Falcon further. Also a billboard.",
            },
            ultimate: {
                slot: "ultimate",
                id: "barrage",
                name: "Starlink",
                damage: 15,
                cooldownMs: 0,
                windupMs: 500,
                recoveryMs: 400,
                hit: { kind: "ground_zone", radius: 12, durationMs: 3000 },
                effects: [{ kind: "repeat", count: 6, intervalMs: 500 }],
                description: "Six shells over three seconds across a wide area.",
            },
        },
    },
};
export const CHARACTER_IDS = Object.keys(CHARACTERS);
export function isCharacterId(v) {
    return Object.prototype.hasOwnProperty.call(CHARACTERS, v);
}
export function getCharacter(id) {
    const c = CHARACTERS[id];
    if (!c)
        throw new Error(`Unknown character: ${id}`);
    return c;
}
/** MVP roster. M2 opens the rest. */
export const MVP_CHARACTERS = ["volt", "monolith", "talon"];
//# sourceMappingURL=characters.js.map